import { useState, useEffect } from 'react';
import { captureSentryException } from '@/lib/sentry';
import { Link, useNavigate } from 'react-router-dom';
import { useGroups, useCreateGroup, useJoinGroup } from '@/hooks/useGroups';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users } from 'lucide-react';
import { MyLoanRequests } from '@/components/MyLoanRequests';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { T, SHADOW, RADIUS } from '@/lib/tokens';
import { Layout } from '@/components/design/Layout';
import { useSubscription } from '@/hooks/useSubscription';

/**
 *
 */
export default function Dashboard() {
  const { data: groups, isLoading, isError } = useGroups();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ['profile-onboarding', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('has_completed_onboarding, display_name')
        .eq('user_id', user!.id)
        .single();
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (isLoading || !profile) return;
    if ((profile as any).has_completed_onboarding) return;

    if (!groups || groups.length === 0) {
      navigate('/onboarding/welcome', { replace: true });
    } else {
      if (user) {
        supabase
          .from('profiles')
          .update({ has_completed_onboarding: true } as any)
          .eq('user_id', user.id)
          .then(({ error: updateError }) => {
            if (updateError) {
              console.error('Failed to complete onboarding:', updateError);
              toast.error('Something went wrong finishing setup. Please try refreshing.');
              return;
            }
            queryClient.invalidateQueries({ queryKey: ['profile-onboarding'] });
          })
          .catch((err) => {
            captureSentryException(err);
            console.error('Onboarding update failed:', err);
            toast.error('Something went wrong finishing setup. Please try refreshing.');
          });
      }
    }
  }, [isLoading, profile, groups, user, navigate, queryClient]);

  const createGroup = useCreateGroup();
  const joinGroup = useJoinGroup();
  const { maxGroups, isPaid, isOnTrial, trialDaysRemaining } = useSubscription();

  const { data: groupCount = 0 } = useQuery({
    queryKey: ['my-groups-count', user?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('group_memberships')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user!.id);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
  });

  const atGroupLimit = !isPaid && maxGroups > 0 && groupCount >= maxGroups;
  const isGroupsOverLimit = !isPaid && maxGroups > 0 && groupCount > maxGroups;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [groupRowHovered, setGroupRowHovered] = useState<string | null>(null);
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(
    () => sessionStorage.getItem('trialBannerDismissed') === 'true'
  );

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    try {
      await createGroup.mutateAsync({
        name: newGroupName.trim(),
        description: newGroupDescription.trim() || undefined,
      });
      toast.success('Group created!');
      setCreateDialogOpen(false);
      setNewGroupName('');
      setNewGroupDescription('');
    } catch {
      // Error toast handled by useCreateGroup onError
    }
  };

  const handleJoinGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    try {
      await joinGroup.mutateAsync(inviteCode.trim());
      toast.success('Joined group successfully!');
      setJoinDialogOpen(false);
      setInviteCode('');
    } catch {
      // Error toast handled by useJoinGroup onError
    }
  };

  const actionButtons = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
      <button
        onClick={() => setJoinDialogOpen(true)}
        style={{
          flex: 1,
          padding: '9px 14px',
          border: `1px solid ${T.accent}`,
          borderRadius: RADIUS.md,
          background: T.surface,
          color: T.accent,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Join Group
      </button>
      <button
        onClick={() => setCreateDialogOpen(true)}
        style={{
          flex: 1,
          padding: '9px 14px',
          border: 'none',
          borderRadius: RADIUS.md,
          background: T.accent,
          color: T.white,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
          boxShadow: SHADOW.btn.accent,
        }}
      >
        + Create Group
      </button>
    </div>
  );

  const headerContent = <>{actionButtons}</>;

  return (
    <>
      <Layout title="Groups" headerContent={headerContent}>
        {isOnTrial && trialDaysRemaining !== null && trialDaysRemaining <= 3 && !trialBannerDismissed && (
          <div
            style={{
              background: T.accentSoft,
              border: `1px solid ${T.accentBorder}`,
              borderRadius: RADIUS.lg,
              padding: '10px 14px',
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.5 }}>
              Your free trial ends in {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'}.{' '}
              <Link
                to="/account"
                style={{ color: T.accent, fontWeight: 600, textDecoration: 'none' }}
              >
                Upgrade to keep all your items
              </Link>
            </p>
            <button
              onClick={() => {
                sessionStorage.setItem('trialBannerDismissed', 'true');
                setTrialBannerDismissed(true);
              }}
              aria-label="Dismiss trial banner"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: T.textMuted,
                fontSize: 16,
                padding: 4,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              &times;
            </button>
          </div>
        )}
        <MyLoanRequests />
        {/* Groups list */}
        <div style={{ paddingTop: 8 }}>
          {isLoading ? (
            <div
              style={{ textAlign: 'center', color: T.textMuted, padding: '48px 0', fontSize: 14 }}
            >
              Loading groups…
            </div>
          ) : isError ? (
            <div style={{ textAlign: 'center', padding: '64px 20px' }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: T.danger, margin: '0 0 6px' }}>
                Failed to load groups
              </p>
              <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
                Please check your connection and try again.
              </p>
            </div>
          ) : groups?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '64px 20px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: 16,
                  opacity: 0.25,
                }}
              >
                <Users size={52} color={T.textMuted} />
              </div>
              <p style={{ fontSize: 17, fontWeight: 600, color: T.text, margin: '0 0 8px' }}>
                You're not in any groups yet
              </p>
              <p style={{ fontSize: 14, color: T.textSec, lineHeight: 1.65, margin: '0 0 24px' }}>
                Create a group to start sharing, or join one with an invite link.
              </p>
              <button
                onClick={() => setCreateDialogOpen(true)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px',
                  borderRadius: RADIUS.md,
                  background: T.accent,
                  color: T.white,
                  border: 'none',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: SHADOW.btn.accent,
                  marginBottom: 12,
                }}
              >
                + Create Group
              </button>
              <button
                type="button"
                onClick={() => setJoinDialogOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: T.accent,
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  padding: 0,
                }}
              >
                Got an invite link? Join a group
              </button>
            </div>
          ) : (
            <>
              {groups?.map((group, idx, arr) => (
                <Link key={group.id} to={`/groups/${group.id}`} style={{ textDecoration: 'none' }}>
                  <div
                    onMouseEnter={() => setGroupRowHovered(group.id)}
                    onMouseLeave={() => setGroupRowHovered(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: 16,
                      borderRadius: RADIUS.lg,
                      cursor: 'pointer',
                      transition: 'box-shadow 0.12s ease, transform 0.12s ease',
                      background: T.surface,
                      border: `1px solid ${T.border}`,
                      boxShadow: groupRowHovered === group.id ? SHADOW.lg : SHADOW.md,
                      transform: groupRowHovered === group.id ? 'translateY(-1px)' : 'none',
                      marginBottom: idx < arr.length - 1 ? 8 : 0,
                    }}
                  >
                    {/* Group icon */}
                    <div
                      style={{
                        width: 46,
                        height: 46,
                        borderRadius: RADIUS.md,
                        flexShrink: 0,
                        background: T.accentSoft,
                        border: `1px solid ${T.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22,
                      }}
                    >
                      👥
                    </div>

                    {/* Text */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}
                      >
                        {group.name}
                      </div>
                      {group.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: T.textMuted,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {group.description}
                        </div>
                      )}
                    </div>

                    {/* Chevron */}
                    <span style={{ color: T.textMuted, fontSize: 16, flexShrink: 0 }}>›</span>
                  </div>
                </Link>
              ))}
            </>
          )}
        </div>
      </Layout>

      {/* Join Group Dialog */}
      <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Join a Group</DialogTitle>
            <DialogDescription>Enter the invite code shared by a group member.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleJoinGroup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="inviteCode">Invite Code</Label>
              <Input
                id="inviteCode"
                placeholder="Enter code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
              />
            </div>
            {atGroupLimit && (
              <p style={{ fontSize: 13, color: T.warning, padding: '4px 0', lineHeight: 1.5, margin: 0 }}>
                {isGroupsOverLimit
                  ? <>Your free plan includes {maxGroups} group, but you belong to {groupCount}. Your groups are safe. To join new ones, <span onClick={() => navigate('/account')} style={{ color: T.accent, textDecoration: 'underline', cursor: 'pointer' }}>upgrade your plan</span> or leave a group.</>
                  : <>You've reached your group limit ({groupCount} of {maxGroups}). <span onClick={() => navigate('/account')} style={{ color: T.accent, textDecoration: 'underline', cursor: 'pointer' }}>Upgrade your plan</span> to join more groups.</>
                }
              </p>
            )}
            <button
              type="submit"
              disabled={joinGroup.isPending || atGroupLimit}
              style={{
                width: '100%',
                padding: '13px',
                borderRadius: RADIUS.md,
                background: T.accent,
                color: T.white,
                border: 'none',
                fontSize: 15,
                fontWeight: 600,
                cursor: joinGroup.isPending || atGroupLimit ? 'default' : 'pointer',
                fontFamily: 'inherit',
                opacity: joinGroup.isPending || atGroupLimit ? 0.7 : 1,
                boxShadow: SHADOW.btn.accent,
              }}
            >
              {joinGroup.isPending ? 'Joining…' : 'Join Group'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a New Group</DialogTitle>
            <DialogDescription>Start a new lending circle with your friends.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateGroup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="groupName">Group Name</Label>
              <Input
                id="groupName"
                placeholder="e.g., Book Club, Tool Share"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="groupDescription">Description (optional)</Label>
              <Input
                id="groupDescription"
                placeholder="What's this group for?"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
              />
            </div>
            {atGroupLimit && (
              <p style={{ fontSize: 13, color: T.warning, padding: '4px 0', lineHeight: 1.5, margin: 0 }}>
                {isGroupsOverLimit
                  ? <>Your free plan includes {maxGroups} group, but you belong to {groupCount}. Your groups are safe. To create new ones, <span onClick={() => navigate('/account')} style={{ color: T.accent, textDecoration: 'underline', cursor: 'pointer' }}>upgrade your plan</span> or leave a group.</>
                  : <>You've reached your group limit ({groupCount} of {maxGroups}). <span onClick={() => navigate('/account')} style={{ color: T.accent, textDecoration: 'underline', cursor: 'pointer' }}>Upgrade your plan</span> to create more groups.</>
                }
              </p>
            )}
            <button
              type="submit"
              disabled={createGroup.isPending || atGroupLimit}
              style={{
                width: '100%',
                padding: '13px',
                borderRadius: RADIUS.md,
                background: T.accent,
                color: T.white,
                border: 'none',
                fontSize: 15,
                fontWeight: 600,
                cursor: createGroup.isPending || atGroupLimit ? 'default' : 'pointer',
                fontFamily: 'inherit',
                opacity: createGroup.isPending || atGroupLimit ? 0.7 : 1,
                boxShadow: SHADOW.btn.accent,
              }}
            >
              {createGroup.isPending ? 'Creating…' : 'Create Group'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
